import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { resetPasswordMail } from "../redux/actions/auth";
import ReCAPTCHA from "react-google-recaptcha";

const schema = yup.object({
  email: yup
    .string()
    .email("Please provide valid email address.")
    .required("Email is required."),
});

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const disabledCss =
    " disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed";

  const [reCaptchaToken, setReCaptchaToken] = useState();
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);

  const captchaRef = useRef(null);

  // useEffect to load the google reCaptcha script once component is fully rendered
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [])

  // Function to store the captcha token
  const handleCaptchaClick = (token) => {
    setReCaptchaToken(token);
    setIsCaptchaChecked(!isCaptchaChecked);
  };

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      email: data.email,
      captchaToken: reCaptchaToken,
    };

    dispatch(
      resetPasswordMail(payload, (res) => {
        setLoading(false);
        captchaRef.current.reset()
        setError({
          type: "success",
          message: "If the email ID is registered a reset link is sent.",
        });
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      })
    );
  };

  return (
    <main className="bg-white">
      <div className="relative md:flex">
        {/* Image */}
        <div className="hidden md:block md:w-1/2" aria-hidden="true">
          <img
            className="object-cover object-center w-full h-full"
            src={require("../assets/img/login-image.jpeg")}
            width="760"
            height="1024"
            alt="Authentication"
          />
        </div>
        {/* Content */}
        <div className="md:w-1/2">
          <div className="min-h-screen h-full flex flex-col after:flex-1">
            <div className="flex flex-col justify-center max-w-sm mx-auto px-4 py-8 h-full">
              {/* Header */}
              <div className="flex mb-5">
                <div className="flex items-center">
                  {/* Logo */}
                  <div className="block w-[40px]">
                    <img
                      alt="Logo"
                      src={require("../assets/img/ShiproneLogo.png")}
                    />
                  </div>
                </div>
              </div>
              <h1 className="text-3xl text-slate-800 font-bold mb-6">
                Reset your password
              </h1>

              <div className="my-3">
                {!error ? (
                  ""
                ) : (
                  <div
                    className={` ${
                      error.type === "error"
                        ? "bg-amber-100 text-amber-600"
                        : "bg-green-600 text-white"
                    } px-3 py-2 rounded`}
                  >
                    <svg
                      className="inline w-3 h-3 shrink-0 fill-current mr-2"
                      viewBox="0 0 12 12"
                    >
                      <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                    </svg>
                    <span className="text-sm">{error.message}</span>
                  </div>
                )}
              </div>

              {/* Form */}
              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <Controller
                    name="email"
                    defaultValue=""
                    control={control}
                    render={({ field, formState }) => (
                      <div>
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="email"
                        >
                          Email Address <span className="text-rose-500">*</span>
                        </label>
                        <input
                          {...field}
                          className="form-input w-full"
                          type="email"
                        />
                        {formState.errors?.email && (
                          <div className="text-xs text-rose-600 pt-1 pl-1">
                            {formState.errors?.email?.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaClick}
                  ref={captchaRef}
                />

                <div className="flex justify-between space-x-3 mt-6">
                  <Link
                    to="/login"
                    className={
                      "w-full flex items-center btn bg-white hover:bg-slate-100 border border-gray-200 text-gray-600 whitespace-nowrap"
                    }
                  >
                    Back
                  </Link>
                  <button
                    type="submit"
                    disabled={loading || !isCaptchaChecked}
                    className={`w-full flex items-center btn whitespace-nowrap ${
                      loading || !isCaptchaChecked
                        ? "!bg-indigo-300 hover:!bg-indigo-400 cursor-not-allowed text-white"
                        : "!bg-indigo-500 hover:!bg-indigo-600 text-white"
                    }`}
                  >
                    Send Reset Link
                    {loading && (
                      <span className="ml-2">
                        <svg
                          className="animate-spin w-4 h-4 fill-current shrink-0"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                        </svg>
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPassword;
